import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Button from 'components/atoms/Button/Button';
import CardWithTitle from 'components/atoms/Card/CardWithTitle';
import ContentButton from 'components/atoms/ContentButton/ContentButton';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import ReferralsBanner from 'components/molecules/ReferralsBanner/ReferralsBanner';
import CancelAccount from 'components/organisms/CancelAccount/CancelAccount';
import SurveyInvitations from 'components/organisms/ContactPreferences/SurveyInvitations';
import { AuthenticationContext } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { useNamespace } from 'components/templates/Translation/TranslationWrapper';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import { useTranslation } from 'next-i18next';
import React, { MouseEvent, useContext } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { MoreQuery } from './__generated__/MoreQuery.graphql';
import EditIcon from './assets/edit-icon.svg';
import classes from './More.module.scss';

export type Props = {
  className?: string;
};

function EditLink({
  href,
  title,
  subTitle,
}: {
  href: string;
  title: string;
  subTitle?: string | null;
}) {
  return (
    <div className={classes.editLinkCategory}>
      <div className={classes.editLinkDetails}>
        <Heading level={3} className={classes.editLinkTitle}>
          {title}
        </Heading>
        <span>{subTitle}</span>
      </div>
      <A href={href} className={classes.editLink}>
        <EditIcon className={classes.editLinkIcon} />
      </A>
    </div>
  );
}

export default function More({ className }: Props) {
  const namespace = useNamespace();
  const { t } = useTranslation(namespace);

  const data = useLazyLoadQuery<MoreQuery>(
    graphql`
      query MoreQuery {
        walletBalance {
          ...CancelAccount_walletBalance
        }
        userData {
          email
          phoneNumber
          ...ChangeEmail_account
        }
        panelConfiguration {
          registrationOptions {
            phoneNumber
            email
          }
        }
        notificationSubscriptions {
          ...SurveyInvitations_notificationSubscriptions
        }
        referralLink
      }
    `,
    {},
  );

  const { email, phoneNumber } = data.userData!;
  const { logOut } = useContext(AuthenticationContext);

  const { clearCountdown } = useCountdownTimer('secondFactorCountdown');

  React.useEffect(() => {
    clearCountdown();
  }, [clearCountdown]);

  function handleLogout(e: MouseEvent) {
    e.preventDefault();
    logOut('header');
  }

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.content}>
        {data.referralLink && <ReferralsBanner />}
        <CardWithTitle title={'Account Settings'} className={classes.card}>
          <EditLink
            href="/account/change-phone-number"
            title="Mobile Number"
            subTitle={phoneNumber || t('Add mobile number')}
          />
          <EditLink
            href="/account/change-email"
            title="Email Address"
            subTitle={email || t('Add email address')}
          />
          <EditLink href="/account/change-password" title="Account Password" />
        </CardWithTitle>

        <CardWithTitle title={'Survey Invitations'} className={classes.card}>
          <SurveyInvitations notificationsSubscriptionsRef={data.notificationSubscriptions} />
        </CardWithTitle>

        <CardWithTitle
          title={'Help Center'}
          className={classes.card}
          onClick={() => console.log('Go to help center')}
        >
          <ContentButton href={process.env.NEXT_PUBLIC_HELPCENTER_URL || ''} target="_blank">
            <Txt>Search for topics via our FAQ’s or get help from our support team</Txt>
          </ContentButton>
        </CardWithTitle>

        <CardWithTitle title={'Cancel Membership'} className={classes.card}>
          <CancelAccount walletBalanceRef={data.walletBalance!} />
        </CardWithTitle>

        <Button onClick={handleLogout} className={classes.signoutButton}>
          <Txt>Sign Out</Txt>
        </Button>
      </div>
    </div>
  );
}
